import dayjs from 'dayjs'
import {
  formatMinutes,
  formatTime
} from 'screens/Rostering/Reconciliation/reconciliationUtils'
import type {
  PayReportBreakdownPaidTimeMode,
  PayReportDrawerApiRow,
  PayReportDrawerTableRow
} from '../types/payReportDrawer'

function payByToPaidTimeMode(
  payBy: PayReportDrawerApiRow['payBy']
): PayReportBreakdownPaidTimeMode | undefined {
  if (payBy == null) {
    return undefined
  }
  const key = String(payBy).toUpperCase()
  if (key === 'DONT_PAY') {
    return 'not_paid'
  }
  if (key === 'PLANNED_TIME') {
    return 'planned'
  }
  if (
    key === 'ACTUAL_TIME' ||
    key === 'CUSTOM_PAID_TIME' ||
    key === 'CUSTOM_AMOUNT'
  ) {
    return 'actual'
  }
  return undefined
}

function getPayRateTypeLabel(
  row: PayReportDrawerApiRow
): 'GBP/hour' | 'GBP fixed' {
  const type = row.payRateType?.toUpperCase()
  if (type === 'EVENT' || type === 'FIXED') {
    return 'GBP fixed'
  }
  if (row.payBy === 'custom_amount' || row.payBy === 'CUSTOM_AMOUNT') {
    return 'GBP fixed'
  }
  return 'GBP/hour'
}

function formatPayRateDisplay(row: PayReportDrawerApiRow): string {
  if (row.payBy === 'custom_amount' || row.payBy === 'CUSTOM_AMOUNT') {
    return '-'
  }
  if (!row.payRate || row.payRate === '-') {
    return '-'
  }
  return `${row.payRate} ${getPayRateTypeLabel(row)}`
}

function toIso(value: Date | string | null | undefined): string | null {
  if (!value) {
    return null
  }
  return dayjs(value).toISOString()
}

export function mapDrawerApiRowsToTable(
  rows: PayReportDrawerApiRow[]
): PayReportDrawerTableRow[] {
  return rows.map((row) => {
    const timezone = row.timezone || 'UTC'
    const plannedStart = toIso(row.plannedTime?.start)
    const plannedEnd = toIso(row.plannedTime?.end)
    const plannedDuration = row.plannedTime?.durationMinutes
    const actualStart = toIso(row.actualTime?.start)
    const actualEnd = toIso(row.actualTime?.end ?? null)
    const actualDuration = row.actualTime?.duration ?? null

    const sortDateTime = plannedStart
      ? dayjs(plannedStart).toISOString()
      : dayjs(row.date).startOf('day').toISOString()

    const paidTimeMode = payByToPaidTimeMode(row.payBy)
    let paidTimeMissed = false
    let paidTimeOngoing = false

    let paidTimeRange: string | undefined
    let paidTimeClockIn: string | undefined
    let paidDuration: string | undefined
    let paidDurationMinutes: number | null = null

    if (paidTimeMode === 'actual') {
      if (!actualStart) {
        paidTimeMissed = true
      } else if (!actualEnd || actualDuration == null) {
        paidTimeOngoing = true
        paidTimeClockIn = formatTime(actualStart, timezone)
      } else {
        paidTimeRange = `${formatTime(actualStart, timezone)}-${formatTime(actualEnd, timezone)}`
        if (Number.isFinite(actualDuration)) {
          paidDuration = formatMinutes(actualDuration)
          paidDurationMinutes = actualDuration
        }
      }
    } else {
      if (plannedStart && plannedEnd) {
        paidTimeRange = `${formatTime(plannedStart, timezone)}-${formatTime(plannedEnd, timezone)}`
      }
      if (plannedDuration != null && Number.isFinite(plannedDuration)) {
        paidDuration = formatMinutes(plannedDuration)
        paidDurationMinutes = plannedDuration
      }
    }

    const subject = row.subject
      ? {
          id: row.subject.id,
          name: row.subject.name,
          picture: row.subject.picture,
          deleted: row.subject.deleted,
          status: row.subject.status
        }
      : null

    return {
      id: row.instanceId,
      date: row.date,
      sortDateTime,
      timezone,
      serviceUser: subject,
      eventTypeName: row.eventType.name,
      eventKind: row.type === 'visit' ? 'visit' : 'shift',
      paidTimeMode,
      paidTimeMissed,
      paidTimeOngoing,
      paidTimeRange,
      paidTimeClockIn,
      paidDuration,
      paidDurationMinutes,
      payRate: formatPayRateDisplay(row),
      payAmount: row.careDeliveryPay ?? '-',
      travelTimePay: row.travelTimePay ?? '0.00',
      mileagePay: row.mileagePay ?? '0.00',
      totalPay: row.totalPay ?? '-'
    }
  })
}
